/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Sparkle, PaperPlaneTilt, X, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Floating "Ask AI about <celebrity>" widget for the celebrity detail page.
 * Pinned bottom-right (mobile-aware). Opens an inline conversation panel
 * that calls POST /celebrities/{slug}/ask-ai grounded in that celeb's
 * Swiss-Ephemeris chart.
 *
 * Stateless on the server (each request includes the chart context) so the
 * panel doesn't need to persist anything — just the in-memory conversation
 * shown to the user while they ask follow-ups.
 */
const SUGGESTIONS = [
  "Why did this peak happen in their rise year?",
  "What does the D9 say about their marriage?",
  "Which dasha lord made them famous?",
  "What planetary combination drove their wealth?",
  "Walk me through their Saturn return.",
];

export default function CelebrityAskAIWidget({ slug, celebrityName }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);    // {role: 'user'|'ai', text}
  const [usage, setUsage] = useState(null);    // {asked_count, daily_limit}
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, busy]);

  const ask = async (raw) => {
    const q = (raw || question || "").trim();
    if (!q || busy) return;
    if (q.length < 4) { toast.error("Ask a real question (4+ chars)."); return; }
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: q }]);
    setQuestion("");
    try {
      const { data } = await api.post(`/celebrities/${slug}/ask-ai`, { question: q });
      setThread((t) => [...t, { role: "ai", text: data.answer }]);
      setUsage({ asked_count: data.asked_count, daily_limit: data.daily_limit });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't reach the AI. Try again in a moment.";
      setThread((t) => [...t, { role: "ai", text: `⚠️ ${detail}`, isError: true }]);
      if (err?.response?.status === 429) {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    ask();
  };

  if (!slug) return null;

  return (
    <>
      {/* Pulse button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Ask AI about ${celebrityName || "this celebrity"}`}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 group rounded-full shadow-2xl flex items-center gap-2 pl-3 pr-4 sm:pl-4 sm:pr-5 py-3 transition-all hover:scale-105"
          style={{
            background: "linear-gradient(135deg, #B45309 0%, #F59E0B 50%, #FCD34D 100%)",
            color: "#1F1306",
            boxShadow: "0 12px 36px rgba(245,158,11,0.55), 0 0 0 1px rgba(255,232,140,0.4)",
          }}
          data-testid="ask-ai-fab">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-200 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <Sparkle size={16} weight="fill" />
          <span className="text-xs sm:text-sm font-semibold tracking-tight">
            Ask AI<span className="hidden sm:inline"> about this chart</span>
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-2 bottom-2 sm:inset-auto sm:bottom-6 sm:right-6 z-50 sm:w-[400px] max-h-[88vh] rounded-2xl flex flex-col overflow-hidden"
          style={{
            background: "#15110F",
            border: "1px solid #3F2D1B",
            boxShadow: "0 24px 72px rgba(0,0,0,0.6), 0 0 0 1px rgba(245,158,11,0.18)",
          }}
          data-testid="ask-ai-panel">
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between gap-2"
            style={{
              background: "linear-gradient(135deg, #B45309 0%, #92400E 100%)",
              color: "#FFF7DD",
            }}>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-90 flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> Ask AI · grounded in the chart
              </div>
              <div className="text-sm font-semibold truncate">{celebrityName || "This celebrity"}</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Ask AI"
              className="opacity-80 hover:opacity-100 transition-opacity"
              data-testid="ask-ai-close">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Thread */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
            style={{ color: "#FAEFE6" }}
            data-testid="ask-ai-thread">
            {thread.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm font-light leading-relaxed" style={{ color: "#C5B5A4" }}>
                  Ask anything about {celebrityName ? <b style={{ color: "#FCD34D" }}>{celebrityName}'s</b> : "their"} chart —
                  events, dasha timing, marriage, wealth, public role. The AI reads their
                  exact D1 + D9 + Vimshottari for the answer.
                </p>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#9C8B7F" }}>
                  Try one of these:
                </div>
                <div className="flex flex-col gap-1.5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => ask(s)}
                      disabled={busy}
                      className="text-left text-xs font-light px-3 py-2 rounded-lg transition-all disabled:opacity-50"
                      style={{
                        background: "#221915",
                        border: "1px solid #3F2D1B",
                        color: "#FAEFE6",
                      }}
                      data-testid={`ask-ai-suggestion-${i}`}>
                      <span style={{ color: "#FCD34D" }}>→</span> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {thread.map((m, i) => (
              <div
                key={i}
                className={`rounded-xl p-3 text-sm font-light leading-relaxed ${
                  m.role === "user" ? "ml-auto" : "mr-auto"
                }`}
                style={{
                  maxWidth: "92%",
                  background: m.role === "user" ? "#3F2D1B" : (m.isError ? "#3F1A1A" : "#1F1813"),
                  color: m.role === "user" ? "#FFF7DD" : "#FAEFE6",
                  border: m.role === "user" ? "1px solid #B45309" : "1px solid #3F2D1B",
                }}
                data-testid={`ask-ai-msg-${m.role}`}>
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="mr-auto rounded-xl p-3 text-sm font-mono"
                style={{ maxWidth: "92%", background: "#1F1813", border: "1px solid #3F2D1B", color: "#9C8B7F" }}
                data-testid="ask-ai-typing">
                Reading the chart…
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            className="p-3 flex items-end gap-2"
            style={{ background: "#0F0C0A", borderTop: "1px solid #3F2D1B" }}>
            <textarea
              ref={inputRef}
              rows={1}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
              placeholder={`Ask about ${celebrityName || "this celebrity"}'s chart…`}
              className="flex-1 px-3 py-2 rounded-lg resize-none text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/60"
              style={{
                background: "#221915", border: "1px solid #3F2D1B",
                color: "#FAEFE6", maxHeight: "120px",
              }}
              maxLength={600}
              data-testid="ask-ai-input"
            />
            <button
              type="submit"
              disabled={busy || question.trim().length < 4}
              aria-label="Send"
              className="rounded-lg p-2.5 transition-all disabled:opacity-40"
              style={{
                background: "linear-gradient(135deg, #B45309 0%, #F59E0B 100%)",
                color: "#1F1306",
              }}
              data-testid="ask-ai-send">
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </form>
          {usage && (
            <div className="px-3 pb-2 text-[10px] font-mono text-right" style={{ color: "#6A5950" }}>
              {usage.asked_count}/{usage.daily_limit} questions used in last 24h
            </div>
          )}
        </div>
      )}
    </>
  );
}
